/**
 * Image Utilities for Firebase Firestore
 * 
 * This module provides all image handling operations for uploading and retrieving
 * images stored as base64 in Firestore under: users/{userId}/images
 * 
 * Features:
 * - Convert images to base64 format
 * - Validate image size (max 2MB)
 * - Upload images to Firestore
 * - Retrieve images from Firestore
 * - Delete images from Firestore
 * - List all user images with metadata
 * 
 * Data structure:
 * users/{userId}/images/{imageId} -> {
 *   base64: "data:image/jpeg;base64,/9j/4AAQSkZJRg...",
 *   filename: "image1.jpg",
 *   size: 1024000,
 *   uploadedAt: Timestamp,
 *   type: "image/jpeg"
 * }
 */

import { db } from '../../../../config/firebaseConfig';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

// Constants
const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1MB in bytes
const MIN_IMAGE_SIZE = 1024; // 1KB minimum
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const MIN_IMAGE_DIMENSION = 10; // Minimum width/height in pixels
const MAX_IMAGE_DIMENSION = 10000; // Maximum width/height in pixels

/**
 * Validate image file synchronously
 * Checks MIME type, extension, and file size
 * 
 * @param {File} file - The image file to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 */
const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Check file extension
  const filename = file.name.toLowerCase();
  const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => filename.endsWith(ext));
  if (!hasValidExtension) {
    return {
      valid: false,
      error: `Invalid file extension. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`,
    };
  }

  // Check file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid MIME type: ${file.type}. Allowed types: JPEG, PNG, WebP`,
    };
  }

  // Check minimum file size
  if (file.size < MIN_IMAGE_SIZE) {
    return {
      valid: false,
      error: 'File is too small. Minimum size is 1KB',
    };
  }

  // Check maximum file size
  if (file.size > MAX_IMAGE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `Image size (${sizeMB}MB) exceeds maximum limit of 1MB`,
    };
  }

  return { valid: true, error: null };
};

/**
 * Validate image dimensions asynchronously
 * Loads the image and checks if dimensions are valid
 * 
 * @param {File} file - The image file to validate
 * @returns {Promise<Object>} - { valid: boolean, width: number, height: number, error: string|null }
 */
export const validateImageDimensions = (file) => {
  return new Promise((resolve) => {
    if (!file) {
      resolve({ valid: false, width: 0, height: 0, error: 'No file selected' });
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const { width, height } = img;

        // Check minimum dimensions
        if (width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION) {
          resolve({
            valid: false,
            width,
            height,
            error: `Image dimensions too small (${width}x${height}). Minimum is ${MIN_IMAGE_DIMENSION}x${MIN_IMAGE_DIMENSION}px`,
          });
          return;
        }

        // Check maximum dimensions
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          resolve({
            valid: false,
            width,
            height,
            error: `Image dimensions too large (${width}x${height}). Maximum is ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION}px`,
          });
          return;
        }

        resolve({ valid: true, width, height, error: null });
      };

      img.onerror = () => {
        resolve({
          valid: false,
          width: 0,
          height: 0,
          error: 'Failed to load image. File may be corrupted or not a valid image',
        });
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      resolve({
        valid: false,
        width: 0,
        height: 0,
        error: 'Failed to read file',
      });
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Convert file to base64 string
 * 
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 encoded string with data URI prefix
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result); // Returns "data:image/jpeg;base64,..."
    };

    reader.onerror = (error) => {
      reject(new Error(`Failed to read file: ${error.message}`));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Upload a single image to Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {File} imageFile - The image file to upload
 * @param {string} [customFilename] - Optional custom filename (defaults to original)
 * @returns {Promise<Object>} - { success: boolean, imageId: string, data: Object, error: string }
 * @throws {Error} - If validation or upload fails
 * 
 * @example
 * const result = await uploadImage(userId, fileInput.files[0]);
 * if (result.success) {
 *   console.log('Image uploaded:', result.imageId);
 * }
 */
export const uploadImage = async (userId = null, imageFile, customFilename = null) => {
  try {
    // Validate image
    const validation = validateImageFile(imageFile);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }

    // Convert to base64
    const base64 = await fileToBase64(imageFile);

    // Generate image ID
    const imageId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create image document
    const imageData = {
      base64,
      filename: customFilename || imageFile.name,
      size: imageFile.size,
      type: imageFile.type,
      uploadedAt: serverTimestamp(),
      isPublished: false,
      publicRefsCount: 0,
    };

    // Save to Firestore
    const imageRef = userId ? doc(db, 'users', userId, 'images', imageId) : doc(db,'images', imageId);
    await setDoc(imageRef, imageData);

    return {
      success: true,
      imageId,
      data: imageData,
      message: `Image "${imageData.filename}" uploaded successfully`,
    };
  } catch (error) {
    console.error('Error uploading image:', error.message);
    return {
      success: false,
      error: `Failed to upload image: ${error.message}`,
    };
  }
};

/**
 * Upload multiple images to Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {FileList|Array} imageFiles - Collection of image files to upload
 * @returns {Promise<Object>} - { success: boolean, uploaded: Array, failed: Array }
 * 
 * @example
 * const result = await uploadMultipleImages(userId, fileInput.files);
 */
export const uploadMultipleImages = async (userId, imageFiles) => {
  const uploaded = [];
  const failed = [];

  for (const file of imageFiles) {
    const result = await uploadImage(userId, file);
    if (result.success) {
      uploaded.push({ imageId: result.imageId, filename: result.data.filename });
    } else {
      failed.push({ filename: file.name, error: result.error });
    }
  }

  return {
    success: failed.length === 0,
    uploaded,
    failed,
    message: `Uploaded ${uploaded.length} image(s). ${failed.length > 0 ? `${failed.length} failed.` : ''}`,
  };
};

/**
 * Retrieve a single image from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} imageId - The ID of the image to retrieve
 * @returns {Promise<Object|null>} - Image data or null if not found
 * @throws {Error} - If retrieval fails
 * 
 * @example
 * const image = await getImage(userId, 'image123');
 * if (image) {
 *   img.src = image.base64; // Use directly in img tag
 * }
 */
export const getImage = async (userId, imageId) => {
  try {
    const imageRef = doc(db, 'users', userId, 'images', imageId);
    const docSnap = await getDoc(imageRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    }

    return null;
  } catch (error) {
    console.error('Error retrieving image:', error.message);
    throw new Error(`Failed to retrieve image: ${error.message}`);
  }
};

export const getSharedImage = async (imageId) => {
  try {
    const imageRef = doc(db, 'images', imageId);
    const docSnap = await getDoc(imageRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    }

    return null;
  } catch (error) {
    console.error('Error retrieving image:', error.message);
    throw new Error(`Failed to retrieve image: ${error.message}`);
  }
};

/**
 * Retrieve all images for a user
 * 
 * @param {string} userId - User's unique ID
 * @returns {Promise<Array>} - Array of image objects with metadata
 * @throws {Error} - If retrieval fails
 * 
 * @example
 * const images = await getUserImages(userId);
 * images.forEach(img => {
 *   console.log(img.filename, img.base64);
 * });
 */
export const getUserImages = async (userId) => {
  try {
    const imagesRef = collection(db, 'users', userId, 'images');
    const q = query(imagesRef, orderBy('uploadedAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const images = [];
    querySnapshot.forEach((doc) => {
      images.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    console.log('Images retrieved from Firestore (getUserImages):', images);
    return images;
  } catch (error) {
    console.error('Error retrieving user images:', error.message);
    throw new Error(`Failed to retrieve images: ${error.message}`);
  }
};

export const getSharedImages = async () => {
  try {
    const imagesRef = collection(db, 'images');
    const q = query(imagesRef, orderBy('uploadedAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const images = [];
    querySnapshot.forEach((doc) => {
      images.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    console.log('Shared images retrieved from Firestore (getSharedImages):', images);
    return images;
  } catch (error) {
    console.error('Error retrieving user images:', error.message);
    throw new Error(`Failed to retrieve images: ${error.message}`);
  }
};

/**
 * Delete a single image from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} imageId - The ID of the image to delete
 * @returns {Promise<Object>} - { success: boolean, message: string, error: string }
 * 
 * @example
 * const result = await deleteImage(userId, 'image123');
 */
export const deleteImage = async (userId, imageId) => {
  try {
    const imageRef = doc(db, 'users', userId, 'images', imageId);
    await deleteDoc(imageRef);

    return {
      success: true,
      message: 'Image deleted successfully',
    };
  } catch (error) {
    console.error('Error deleting image:', error.message);
    return {
      success: false,
      error: `Failed to delete image: ${error.message}`,
    };
  }
};

/**
 * Delete multiple images from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {Array<string>} imageIds - Array of image IDs to delete
 * @returns {Promise<Object>} - { success: boolean, deleted: Array, failed: Array }
 */
export const deleteMultipleImages = async (userId, imageIds) => {
  const deleted = [];
  const failed = [];

  for (const imageId of imageIds) {
    const result = await deleteImage(userId, imageId);
    if (result.success) {
      deleted.push(imageId);
    } else {
      failed.push({ imageId, error: result.error });
    }
  }

  return {
    success: failed.length === 0,
    deleted,
    failed,
    message: `Deleted ${deleted.length} image(s). ${failed.length > 0 ? `${failed.length} failed.` : ''}`,
  };
};

/**
 * Get image metadata without the base64 data (for listings)
 * Useful for displaying image galleries without loading full base64 strings
 * 
 * @param {string} userId - User's unique ID
 * @returns {Promise<Array>} - Array of image metadata objects
 */
export const getUserImageMetadata = async (userId) => {
  try {
    const images = await getUserImages(userId);
    
    return images.map((img) => ({
      id: img.id,
      filename: img.filename,
      size: img.size,
      type: img.type,
      uploadedAt: img.uploadedAt,
      // Don't include base64 in metadata-only queries
    }));
  } catch (error) {
    console.error('Error retrieving image metadata:', error.message);
    throw new Error(`Failed to retrieve image metadata: ${error.message}`);
  }
};

export const getSharedImageMetadata = async () => {
  try {
    const images = await getSharedImages();
    
    return images.map((img) => ({
      id: img.id,
      filename: img.filename,
      size: img.size,
      type: img.type,
      uploadedAt: img.uploadedAt,
      // Don't include base64 in metadata-only queries
    }));
  } catch (error) {
    console.error('Error retrieving image metadata:', error.message);
    throw new Error(`Failed to retrieve image metadata: ${error.message}`);
  }
};

/**
 * Get formatted size display for image
 * 
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string (e.g., "1.5MB")
 */
export const formatImageSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Validate base64 image size
 * 
 * @param {string} base64String - Base64 encoded string (with or without data URI prefix)
 * @returns {Object} - { valid: boolean, size: number, error: string|null }
 */
export const validateBase64Size = (base64String) => {
  try {
    // Remove data URI prefix if present
    const base64Data = base64String.split(',')[1] || base64String;
    
    // Calculate size (rough estimate)
    const size = Math.ceil((base64Data.length * 3) / 4);
    
    if (size > MAX_IMAGE_SIZE) {
      return {
        valid: false,
        size,
        error: `Image size exceeds 2MB limit (${formatImageSize(size)})`,
      };
    }

    return { valid: true, size, error: null };
  } catch (error) {
    return {
      valid: false,
      size: 0,
      error: `Failed to validate base64 size: ${error.message}`,
    };
  }
};

/**
 * Comprehensive image validation
 * Performs all validations: type, extension, size, and dimensions
 * 
 * @param {File} file - The image file to validate
 * @returns {Promise<Object>} - Complete validation result
 */
export const validateImageComprehensive = async (file) => {
  // First, do synchronous validations
  const syncValidation = validateImageFile(file);
  if (!syncValidation.valid) {
    return syncValidation;
  }

  // Then, do asynchronous dimension validation
  const dimensionValidation = await validateImageDimensions(file);
  return dimensionValidation;
};

export const updateImageDescription = async (userId, imageId, description) => {
  try {
    const imageRef = userId ? doc(db, 'users', userId, 'images', imageId) : doc(db, 'images', imageId);
    await updateDoc(imageRef, { description });
    return { success: true };
  } catch (error) {
    console.error(`Error updating description for image ${imageId}:`, error);
    return { success: false, error: error.message };
  }
};

export default {
  uploadImage,
  uploadMultipleImages,
  getImage,
  getSharedImage,
  getUserImages,
  getSharedImages,
  deleteImage,
  deleteMultipleImages,
  getUserImageMetadata,
  fileToBase64,
  formatImageSize,
  validateBase64Size,
  validateImageDimensions,
  validateImageComprehensive,
  updateImageDescription,
};
